// src/views/components/FeaturesGrid.js
import React from 'react';
import { 
  Calendar, FileText, Users, DollarSign, 
  Briefcase, TrendingUp, ArrowUpRight, Route
} from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    title: "Smart Route Optimization",
    desc: "Stop sending your crew across town. Spatchy groups jobs by geography, loads Google Maps with every stop pre-ordered, and scores your route health before the day starts. Fit one more job into every day.",
    icon: <Route className="text-cyan-400" size={32} />,
    colSpan: "md:col-span-3",
    badge: "Most Powerful Feature",
    badgeColor: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20",
    path: "/features/smart-routing"
  },
  {
    title: "Calendar & Dispatching",
    // wave1-fix 6b7d2c8f: guard is a client-side warning for vehicles/equipment only — drop unverified team-member claim, soften "never...again"
    desc: "Drag-and-drop scheduling for your whole crew. Syncs with Microsoft Outlook & Calendar. Resource conflict detection flags a conflict before you double-book a vehicle.",
    icon: <Calendar className="text-blue-400" size={32} />,
    colSpan: "md:col-span-2", 
    path: "/features/calendar-dispatching"
  },
  {
    title: "Quote Builder",
    desc: "Build a detailed estimate, send it to your customer, and get a digital approval — without a single phone call. No more 'did you get my quote?' follow-ups.",
    icon: <FileText className="text-purple-400" size={32} />,
    colSpan: "md:col-span-1",
    path: "/features/quote-builder"
  },
  {
    title: "CRM & Lead Pipeline",
    desc: "Know every customer by name, job history, and what they owe — without digging through texts and notebooks. From first inquiry to final invoice, it's all there.",
    icon: <Users className="text-green-400" size={32} />,
    colSpan: "md:col-span-1",
    path: "/features/client-database"
  },
  {
    title: "Invoicing & Stripe Payments",
    desc: "Convert a completed job to a paid invoice in two clicks. Automated overdue reminders chase payments so you don't have to.",
    icon: <DollarSign className="text-emerald-400" size={32} />,
    colSpan: "md:col-span-2",
    path: "/features/invoicing-payments"
  },
  {
    title: "Job Tracking",
    desc: "Watch your jobs move from Scheduled to In Progress to Paid in real time. Stop calling your crew to ask if the job is done.",
    icon: <Briefcase className="text-orange-400" size={32} />,
    colSpan: "md:col-span-2",
    path: "/features/job-tracking"
  },
  {
    title: "Team & HR",
    desc: "Add your whole crew at no extra charge. Timesheets, employee files, time-off, and payroll prep — without paying per seat like every other platform makes you do.",
    icon: <TrendingUp className="text-pink-400" size={32} />,
    colSpan: "md:col-span-1",
    path: "/features/team-management"
  },
];

const FeaturesGrid = () => {
  return (
    <section className="py-24 px-6 bg-slate-900">
      <div className="max-w-6xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Everything you need to run the job.<br />
            <span className="text-slate-500">Every plan. No upgrades. One price.</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg">
            From the moment a customer calls to the moment you get paid — routing, scheduling, quoting, invoicing, and payroll all in one place. No extra seats. No feature tiers. No surprises.
          </p>
        </div>

        {/* The Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <Link 
              to={f.path}
              key={i} 
              className={`${f.colSpan} bg-slate-800/50 border border-white/5 rounded-2xl p-8 hover:bg-slate-800 transition-all group relative overflow-hidden`}
            >
              {/* Hover Arrow */}
              <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                 <ArrowUpRight className="text-white" />
              </div>

              {/* Badge (routing only) */}
              {f.badge && (
                <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase mb-4 ${f.badgeColor}`}>
                  {f.badge}
                </div>
              )}

              <div className="mb-6 bg-slate-900/50 w-16 h-16 rounded-xl flex items-center justify-center border border-white/5 group-hover:scale-110 transition-transform">
                {f.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{f.title}</h3>
              <p className="text-slate-400 leading-relaxed">{f.desc}</p>
            </Link>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FeaturesGrid;